import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Sparkles, Calendar, Search, Scale, FileSignature,
  Check, Copy, Clock,
} from "lucide-react";

const BLUE = "#1d4ed8";
const BLUE_SOFT = "#eff6ff";
const BLUE_BORDER = "#bfdbfe";

/**
 * HowItWorks
 * Four-step editorial section: Understand -> Track -> Compare -> Act.
 * Each row pairs a left text column (headline + tool chips) with a right
 * interactive demo panel. All demos are client-only fakes with crisp
 * micro-interactions; nothing hits the backend.
 */
export default function HowItWorks() {
  return (
    <section className="bg-white border-y border-gray-200" data-testid="how-it-works">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="mb-14 max-w-2xl">
          <div className="text-[11px] uppercase tracking-[0.22em] mb-3 text-gray-400">
            How it works
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl leading-[1.05] text-gray-900">
            How AidWaive <em className="italic" style={{ color: BLUE }}>works.</em>
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-gray-500">
            From confusion to clarity to action.
          </p>
        </div>

        <div className="space-y-20 sm:space-y-28">
          <Row
            num="01"
            tag="Understand"
            title="Understand your financial aid and costs in plain English."
            tools={[
              { to: "/chat",      label: "AI Chat",            icon: Sparkles },
              { to: "/decoder",   label: "Aid Letter Decoder", icon: FileSignature },
              { to: "/estimator", label: "SAI Estimator",      icon: Scale },
            ]}
            demo={<UnderstandDemo />}
            ctaTo="/chat" ctaLabel="Try the AI Chat"
            testid="hiw-row-understand"
          />
          <Row
            reverse
            num="02"
            tag="Track"
            title="Track deadlines and find real opportunities."
            tools={[
              { to: "/deadlines",    label: "Deadline Tracker",   icon: Calendar },
              { to: "/scholarships", label: "Scholarship Finder", icon: Search },
            ]}
            demo={<TrackDemo />}
            ctaTo="/deadlines" ctaLabel="Check my deadline"
            testid="hiw-row-track"
          />
          <Row
            num="03"
            tag="Compare"
            title="Compare real costs and outcomes before deciding."
            tools={[
              { to: "/compare",  label: "School Comparer",      icon: Scale },
              { to: "/netprice", label: "Net Price Calculator", icon: Sparkles },
              { to: "/loans",    label: "Loan Reality Check",   icon: Clock },
            ]}
            demo={<CompareDemo />}
            ctaTo="/compare" ctaLabel="Compare schools"
            testid="hiw-row-compare"
          />
          <Row
            reverse
            num="04"
            tag="Act"
            title="Generate a professional aid appeal in minutes."
            tools={[
              { to: "/appeal", label: "Appeal Writer", icon: FileSignature },
            ]}
            demo={<ActDemo />}
            ctaTo="/appeal" ctaLabel="Write my appeal"
            testid="hiw-row-act"
          />
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Row scaffold                                                         */
/* ──────────────────────────────────────────────────────────────────── */

function Row({ num, tag, title, tools, demo, reverse, ctaTo, ctaLabel, testid }) {
  return (
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center" data-testid={testid}>
      <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
        <div className="flex items-baseline gap-3 mb-5">
          <span className="font-heading text-5xl leading-none" style={{ color: BLUE }}>{num}</span>
          <span className="text-[11px] uppercase tracking-[0.22em] text-gray-400">{tag}</span>
        </div>
        <h3 className="font-heading text-2xl sm:text-3xl leading-[1.15] text-gray-900 max-w-md">
          {title}
        </h3>
        <div className="mt-6 flex flex-wrap gap-2">
          {tools.map(t => <ToolChip key={t.to} to={t.to} label={t.label} Icon={t.icon} />)}
        </div>
        {ctaTo && (
          <Link to={ctaTo}
            data-testid={`${testid}-cta`}
            className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
            style={{ color: BLUE }}>
            {ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
      <div className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""}`}>{demo}</div>
    </div>
  );
}

function ToolChip({ to, label, Icon }) {
  return (
    <Link to={to}
      className="inline-flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full text-xs font-medium transition-colors"
      style={{ background: BLUE_SOFT, color: "#1e3a8a", border: `1px solid ${BLUE_BORDER}` }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "#dbeafe"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = BLUE_SOFT; }}>
      <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
      {label}
    </Link>
  );
}

function PanelCard({ children, testid }) {
  return (
    <div
      className="rounded-2xl bg-white transition-all"
      style={{
        border: "1px solid #bfdbfe",
        boxShadow: "0 1px 2px rgba(30,58,138,0.06)",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 8px 28px rgba(30,58,138,0.10)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 2px rgba(30,58,138,0.06)"; }}
      data-testid={testid}>
      {children}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Demo 1: Understand — fake AI chat that reveals a structured reading  */
/* ──────────────────────────────────────────────────────────────────── */

const UNDERSTAND_LINES = [
  "I read the letter for Vanderbilt.",
  "Here is what your offer actually says, in plain English:",
];
function UnderstandDemo() {
  const [phase, setPhase] = useState("idle"); // idle | typing | done
  const [shown, setShown] = useState(0);
  const fullText = UNDERSTAND_LINES.join("\n");
  const tRef = useRef(null);

  useEffect(() => () => clearInterval(tRef.current), []);

  const run = () => {
    if (phase === "typing") return;
    setPhase("typing");
    setShown(0);
    let i = 0;
    tRef.current = setInterval(() => {
      i += 2;
      setShown(i);
      if (i >= fullText.length) {
        clearInterval(tRef.current);
        setPhase("done");
      }
    }, 18);
  };

  return (
    <PanelCard testid="hiw-demo-understand">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ background: "#22c55e" }} />
        <span className="text-xs uppercase tracking-[0.18em] text-gray-400">AidWaive AI</span>
      </div>
      <div className="p-5 sm:p-6 space-y-4 min-h-[260px]">
        <div className="flex justify-end">
          <div className="max-w-[80%] rounded-2xl rounded-br-sm px-3.5 py-2 text-sm text-gray-900"
            style={{ background: "#f3f4f6" }}>
            Decode this Vanderbilt offer. Tuition, grants, net cost?
          </div>
        </div>

        {phase !== "idle" && (
          <div className="flex">
            <div className="max-w-[88%] rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm text-gray-900"
              style={{ background: BLUE_SOFT, border: `1px solid ${BLUE_BORDER}` }}>
              <span className="whitespace-pre-line">{fullText.slice(0, shown)}</span>
              {phase === "typing" && <span className="inline-block w-1.5 h-3.5 ml-0.5 align-[-2px] animate-pulse" style={{ background: BLUE }} />}
            </div>
          </div>
        )}

        {phase === "done" && (
          <div className="grid grid-cols-3 gap-3 fade-in">
            <Stat k="Tuition"  v="$86,250" muted />
            <Stat k="Grants"   v="-$61,000" pos />
            <Stat k="Net cost" v="$25,250" big />
          </div>
        )}
      </div>
      <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
        <input
          readOnly
          value="Decode this Vanderbilt offer. Tuition, grants, net cost?"
          className="flex-1 text-sm text-gray-700 bg-transparent outline-none truncate"
          aria-label="Demo prompt"
        />
        <button
          onClick={run}
          disabled={phase === "typing"}
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-xs font-medium disabled:opacity-50 transition-opacity hover:opacity-90"
          style={{ background: BLUE, color: "#fff" }}
          data-testid="hiw-understand-analyze">
          {phase === "done" ? "Run again" : "Analyze"} <Sparkles className="w-3.5 h-3.5" />
        </button>
      </div>
    </PanelCard>
  );
}

function Stat({ k, v, pos, muted, big }) {
  return (
    <div className="rounded-xl px-3 py-2.5"
      style={{ background: big ? BLUE_SOFT : "#f9fafb", border: big ? `1px solid ${BLUE_BORDER}` : "1px solid #c7d2fe" }}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-gray-400">{k}</div>
      <div className={`mt-1 text-base font-semibold tabular-nums ${big ? "" : ""}`}
        style={{ color: pos ? "#15803d" : muted ? "#6b7280" : big ? BLUE : "#111928" }}>
        {v}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Demo 2: Track — deadlines list + reveal scholarships                 */
/* ──────────────────────────────────────────────────────────────────── */

const DEADLINES = [
  { date: "Jun 30",  title: "FAFSA federal deadline",   urgency: "soft" },
  { date: "Mar 1",   title: "Tennessee state aid",      urgency: "hard" },
  { date: "Feb 15",  title: "Vanderbilt institutional", urgency: "hard" },
];
const SCHOLARSHIPS = [
  { name: "Coca-Cola Scholars",        amount: "$20,000", odds: "Reach"   },
  { name: "Gates Scholarship",         amount: "Full ride", odds: "Reach"  },
  { name: "Horatio Alger State",       amount: "$10,000", odds: "Likely"  },
];

function TrackDemo() {
  const [showOpps, setShowOpps] = useState(false);

  return (
    <PanelCard testid="hiw-demo-track">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-xs uppercase tracking-[0.18em] text-gray-400">Your timeline</span>
        </div>
        <button
          onClick={() => setShowOpps(v => !v)}
          className="inline-flex items-center gap-2 px-3 h-8 rounded-full text-xs font-medium transition-colors"
          style={{
            background: showOpps ? BLUE : "#fff",
            color: showOpps ? "#fff" : "#374151",
            border: `1px solid ${showOpps ? BLUE : "#e5e7eb"}`,
          }}
          data-testid="hiw-track-toggle">
          <span
            className="w-7 h-3.5 rounded-full relative transition-colors"
            style={{ background: showOpps ? "rgba(255,255,255,0.35)" : "#e5e7eb" }}>
            <span
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white transition-transform"
              style={{ left: 1, transform: `translate(${showOpps ? 14 : 0}px, -50%)` }}
            />
          </span>
          Show opportunities
        </button>
      </div>

      <ul className="divide-y divide-gray-100">
        {DEADLINES.map((d) => (
          <li key={d.title} className="flex items-center justify-between gap-4 px-5 py-3.5"
            data-testid={`hiw-track-deadline-${d.urgency}`}>
            <div className="flex items-center gap-3 min-w-0">
              <span
                className="shrink-0 w-1.5 h-8 rounded-full"
                style={{ background: d.urgency === "hard" ? "#ef4444" : "#f59e0b" }}
              />
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{d.title}</div>
                <div className="text-xs text-gray-600 mt-0.5">{d.urgency === "hard" ? "Hard deadline" : "Federal window"}</div>
              </div>
            </div>
            <div className="text-sm tabular-nums text-gray-900 shrink-0">{d.date}</div>
          </li>
        ))}
      </ul>

      <div
        className="overflow-hidden transition-all"
        style={{ maxHeight: showOpps ? 360 : 0 }}
        aria-hidden={!showOpps}>
        <div className="px-5 pt-3 pb-5 border-t border-gray-100 space-y-2">
          <div className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-1">Likely matches</div>
          {SCHOLARSHIPS.map((s) => (
            <div key={s.name}
              className="flex items-center justify-between rounded-xl px-3.5 py-3 transition-colors hover:bg-gray-50"
              style={{ border: "1px solid #c7d2fe" }}
              data-testid={`hiw-track-scholarship-${s.name.split(" ")[0].toLowerCase()}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{s.name}</div>
                <div className="text-xs text-gray-500 mt-0.5">Odds, {s.odds.toLowerCase()}</div>
              </div>
              <div className="text-sm font-semibold tabular-nums" style={{ color: BLUE }}>
                {s.amount}
              </div>
            </div>
          ))}
        </div>
      </div>
    </PanelCard>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Demo 3: Compare — two-school table + include loans toggle            */
/* ──────────────────────────────────────────────────────────────────── */

const SCHOOLS = [
  { name: "Vanderbilt University", tuition: 86250, aid: 61000, loans: 5500 },
  { name: "UT Austin",             tuition: 30866, aid: 12500, loans: 3500 },
];

function CompareDemo() {
  const [withLoans, setWithLoans] = useState(false);
  const rows = SCHOOLS.map((s) => {
    const net = s.tuition - s.aid - (withLoans ? s.loans : 0);
    return { ...s, net };
  });
  const minNet = Math.min(...rows.map(r => r.net));

  return (
    <PanelCard testid="hiw-demo-compare">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.18em] text-gray-400">Side by side</span>
        <button
          onClick={() => setWithLoans(v => !v)}
          className="inline-flex items-center gap-2 text-xs font-medium text-gray-700"
          data-testid="hiw-compare-toggle">
          <span
            className="w-9 h-5 rounded-full relative transition-colors"
            style={{ background: withLoans ? BLUE : "#e5e7eb" }}>
            <span
              className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform"
              style={{ left: 2, transform: `translateX(${withLoans ? 16 : 0}px)` }}
            />
          </span>
          Include loans
        </button>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left">
            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-medium text-gray-400">School</th>
            <th className="px-3 py-3 text-[11px] uppercase tracking-[0.16em] font-medium text-gray-400 text-right">Tuition</th>
            <th className="px-3 py-3 text-[11px] uppercase tracking-[0.16em] font-medium text-gray-400 text-right">Aid</th>
            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-medium text-gray-400 text-right">Net cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const best = r.net === minNet;
            return (
              <tr key={r.name}
                className="border-t border-gray-100"
                style={{ background: best ? "#f0fdf4" : "transparent" }}
                data-testid={`hiw-compare-row-${r.name.split(" ")[0].toLowerCase()}`}>
                <td className="px-5 py-4">
                  <div className="text-sm font-medium text-gray-900">{r.name}</div>
                  {best && (
                    <div className="text-[10px] uppercase tracking-[0.12em] mt-0.5 font-medium" style={{ color: "#15803d" }}>
                      best value
                    </div>
                  )}
                </td>
                <td className="px-3 py-4 text-right tabular-nums text-gray-700">${r.tuition.toLocaleString()}</td>
                <td className="px-3 py-4 text-right tabular-nums" style={{ color: "#15803d" }}>-${r.aid.toLocaleString()}</td>
                <td className="px-5 py-4 text-right tabular-nums font-semibold transition-colors"
                  style={{ color: best ? "#15803d" : "#111928" }}>
                  ${r.net.toLocaleString()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-5 py-3 border-t border-gray-100 text-xs text-gray-500">
        {withLoans
          ? "Loans included. Borrowed dollars are subtracted from net cost but must be repaid."
          : "Pure cost view. Toggle loans to see what borrowing would change."}
      </div>
    </PanelCard>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Demo 4: Act — generate appeal paragraph + copy                        */
/* ──────────────────────────────────────────────────────────────────── */

const APPEAL_PARAGRAPH =
  "Dear Vanderbilt Financial Aid Office, since submitting our FAFSA, our family has experienced a meaningful change in circumstances: my parent's hours were reduced in March, lowering household income by approximately 22 percent. I remain committed to enrolling and would be grateful if you could review our file for additional institutional aid. Documentation of the income change is attached. Thank you for your time.";

function ActDemo() {
  const [phase, setPhase] = useState("idle"); // idle | typing | done
  const [shown, setShown] = useState(0);
  const [copied, setCopied] = useState(false);
  const tRef = useRef(null);

  useEffect(() => () => clearInterval(tRef.current), []);

  const generate = () => {
    if (phase === "typing") return;
    setPhase("typing");
    setShown(0);
    setCopied(false);
    let i = 0;
    tRef.current = setInterval(() => {
      i += 4;
      setShown(i);
      if (i >= APPEAL_PARAGRAPH.length) {
        clearInterval(tRef.current);
        setPhase("done");
      }
    }, 14);
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(APPEAL_PARAGRAPH);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <PanelCard testid="hiw-demo-act">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.18em] text-gray-400">Appeal draft</span>
        <span className="text-[11px] text-gray-400">~ 60 sec to send</span>
      </div>
      <div className="p-5 sm:p-6 min-h-[200px]">
        {phase === "idle" ? (
          <div className="flex flex-col items-start gap-3 text-gray-500">
            <div className="text-sm">A clean, formal first draft, ready to edit.</div>
          </div>
        ) : (
          <p className="text-[15px] leading-relaxed text-gray-900">
            {APPEAL_PARAGRAPH.slice(0, shown)}
            {phase === "typing" && (
              <span className="inline-block w-1.5 h-4 ml-0.5 align-[-2px] animate-pulse" style={{ background: BLUE }} />
            )}
          </p>
        )}
      </div>
      <div className="px-5 py-4 border-t border-gray-100 flex items-center gap-3">
        <button
          onClick={generate}
          disabled={phase === "typing"}
          className="inline-flex items-center gap-1.5 px-4 h-9 rounded-md text-xs font-medium disabled:opacity-50 transition-opacity hover:opacity-90"
          style={{ background: BLUE, color: "#fff" }}
          data-testid="hiw-act-generate">
          {phase === "done" ? "Regenerate" : "Generate appeal"}
          <FileSignature className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onCopy}
          disabled={phase !== "done"}
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-xs font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            background: copied ? "#ecfdf5" : "#fff",
            borderColor: copied ? "#a7f3d0" : "#e5e7eb",
            color: copied ? "#15803d" : "#374151",
          }}
          data-testid="hiw-act-copy">
          {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
        </button>
      </div>
    </PanelCard>
  );
}
